/** 
  INFO: Removed distinctUntilChanged to avoid having to write more logic on ngOnChanges.
  distinctUntilChanged((originalObj, newObj) => {
    originalObj[this.datePickerObject.key] = moment(originalObj[this.datePickerObject.key]);
    return JSON.stringify(originalObj) === JSON.stringify(newObj);
  })
*/
import {
  Component, OnInit, Input, forwardRef, Output,
  EventEmitter, OnChanges, SimpleChanges
} from '@angular/core';
import {
  UntypedFormBuilder, UntypedFormGroup, ControlValueAccessor, NG_VALUE_ACCESSOR,
  NG_VALIDATORS, AbstractControl, ValidationErrors, Validators
} from '@angular/forms';
import * as moment from 'moment';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { debounceTime, map } from 'rxjs/operators';

import { datePickerFormat, datePattern } from '../../../utils/validation/datepicker.validator';
import { FormService } from '../../../api/form.service';
import { ErrorHandlerService } from '../../../api/error-handler.service';
import uuid from '../../../utils/uuid';

@UntilDestroy()
@Component({
  selector: 'iv-datepicker-field',
  templateUrl: './datepicker-field.component.html',
  styleUrls: ['./datepicker-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerFieldComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => DatepickerFieldComponent),
    }
  ]
})
export class DatepickerFieldComponent implements OnInit, OnChanges, ControlValueAccessor {

  @Input() fieldId: string;
  @Input() fieldLabel: string;
  @Input() fieldName: string = '';
  @Input() isRequired: boolean = false;
  @Input() disabled: boolean = false;
  @Input() toolTipText: string = '';
  /**
   * INFO: Whether to enable the reactive form compatibility or not,
   * If this is FALSE, the sweet alert won't be available, instead, the validation errors
   * will be provided.
   * */
  @Input() withReactiveForms: boolean = true;
  @Input() datePickerObject: { 'key': string; 'value': string };
  @Input() showValidation: boolean = true;
  /**
   * INFO: Resets the form or nullify it based on a condition
   */
  @Input() resetDatePicker: boolean = false;

  // INFO: The date to open the calendar to initially.
  @Input() startAt: string | null = null;

  // INFO: The minimum valid date.
  @Input() minDate: moment.Moment | null;

  // INFO: The maximum valid date.
  @Input() maxDate: moment.Moment | null;

  // INFO: Angular material design values are: always | never | auto
  @Input() floatLabel: string = 'never';

  @Output() saveDatePicker: EventEmitter<any>;

  uuid = uuid();
  datePickerForm: UntypedFormGroup;

  /** INFO: 
  * It is used when withReactiveForms is False.
  * The user might type in the date without using the date picker, which triggers valueChanges().
  * We need to check 'userIsNotUsingDatePicker' to be able to display validation errors.
  */
  userIsNotUsingDatePicker: boolean = false;

  private innerValue: any = 0;
  private touched: boolean = false;

  onTouchedCallback: () => void = () => { };
  onChangedCallback: (_: any) => void = () => { };

  constructor(
    private fb: UntypedFormBuilder,
    private formService: FormService,
    private errorHandlerService: ErrorHandlerService) {
    this.saveDatePicker = new EventEmitter();
  }

  get value() {
    return this.innerValue;
  }

  // Update the model and then the validations will be executed
  set value(val: any) {
    if (val) {
      this.writeValue(val);
    } else {
      this.writeValue(null);
    }
    this.onChangedCallback(this.innerValue);
  }

  /**
  * INFO: Write to the view when programmatic changes from model to view are requested.
  */
  writeValue(value: any): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
    }
  }

  /**
   * INFO: Write form disabled state to the DOM element (model => view)
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * INFO: Update form when DOM element value changes (view => model)
   */
  registerOnChange(fn: any): void {
    // Store the provided function as an internal method.
    this.onChangedCallback = fn;
  }

  /**
   * INFO: Update form when DOM element is blurred (view => model)
   */
  registerOnTouched(fn: any): void {
    // Store the provided function as an internal method.
    this.onTouchedCallback = fn;
  }

  innerModelChanged(): void {
    this.markAsTouched();
  }

  /**
  * INFO: Along with onTouchedCallback() informs the parent form when this child control has been
  * considered to be touched by the user.
  * */
  markAsTouched(): void {
    if (!this.touched) {
      this.onTouchedCallback();
      this.touched = true;
    }
  }

  /**
  * INFO: The validation errors will be always provided
  * The 'invalidDateFormat' error could be accessed with yourReactiveForm.get('yourFieldName').errors
  */
  validate(control: AbstractControl): ValidationErrors | null {
    let isValid = true;
    if (control.value || (control.value === null && this.touched)) {
      const dateToString: string = datePickerFormat(control.value, 'DD/MM/YYYY');
      isValid = datePattern.test(dateToString);
    }
    return isValid ? null : { 'invalidDateFormat': true };
  }

  ngOnInit(): void {
    if (!this.withReactiveForms) {
      this.setupForm();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Reset DatePickerFn only will work when 'withReactiveForms' is FALSE
    if (changes?.resetDatePicker && changes?.resetDatePicker.currentValue && !this.withReactiveForms) {
      this.resetDatePickerFn();
    }
  }

  setupForm(): void {
    // INFO: All the date fiels coming from BE are strings, we need to convert them to a moment obj
    this.datePickerForm = this.fb.group({
      [this.datePickerObject.key]: [{
        value: this.datePickerObject?.value ? moment(this.datePickerObject?.value) : null,
        disabled: this.disabled
      }]
    });
    if (this.isRequired) {
      this.setRequireValidator();
    }
    this.formService.addLoadedForm(this.datePickerForm, this.uuid);
    if (this.showValidation === true && this.datePickerForm) {
      this.formService.showFormValidations(this.datePickerForm);
    }
    this.setupFormSubscription();
  }

  /**
   * INFO: valueChanges also get triggered when the attributes 'min' and 'max' are used.
   */
  setupFormSubscription(): void {
    this.datePickerForm.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(800),
        map((fieldChanges) => {
          fieldChanges[this.datePickerObject.key] = datePickerFormat(fieldChanges[this.datePickerObject.key]);
          return fieldChanges;
        })
      )
      .subscribe((date: string) => {
        const currentDateValue = date[this.datePickerObject.key];
        if ((currentDateValue || currentDateValue === null) || this.userIsNotUsingDatePicker) {
          if (this.datePickerForm.get(this.datePickerObject.key)?.errors) {
            this.displayErrorMessage();
          } else {
            if (this.datePickerObject.value !== currentDateValue) {
              this.saveDatePicker.emit(date);
            }
          }
          this.userIsNotUsingDatePicker = false;
        }
      });
  }

  displayErrorMessage(): void {
    const matDatepickerMax = this.datePickerForm.get(this.datePickerObject.key)?.errors?.matDatepickerMax || null;
    const matDatepickerMin = this.datePickerForm.get(this.datePickerObject.key)?.errors?.matDatepickerMin || null;
    let errorMessage = `The "${this.fieldName}" is an invalid date.`;

    if (matDatepickerMin) {
      errorMessage = `The "${this.fieldName}" cannot be less than the Start Date.`;
    }

    if (matDatepickerMax) {
      errorMessage = `The "${this.fieldName}" cannot be greater than the End Date.`;
    }

    // TODO: Customize the error message based on the formControl.errors messages
    this.errorHandlerService.customErrorMessage(errorMessage)
      .then((result) => {
        if (result && result.value) {
          this.resetDatePickerFn();
          this.saveDatePicker.emit({ [this.datePickerObject.key]: null });
        }
      });
  }

  setRequireValidator(): void {
    if (this.datePickerForm && this.datePickerForm.get(this.datePickerObject.key)) {
      this.datePickerForm?.get(this.datePickerObject.key)?.setValidators([Validators.required]);
      this.datePickerForm?.get(this.datePickerObject.key)?.updateValueAndValidity();
    }
  }

  resetDatePickerFn(): void {
    if (this.datePickerForm) {
      this.datePickerForm.patchValue(
        { [this.datePickerObject.key]: null },
        { 'emitEvent': false }
      );
    }
  }

  inputDatePicker($event: any) {
    if ($event && $event.inputType === 'insertText') {
      this.userIsNotUsingDatePicker = true;
    } else {
      this.userIsNotUsingDatePicker = false;
    }
  }

  getPlaceHolderValue(val: string): string {
    const userLocale = window.localStorage.getItem('user_locale') || '';
    if (window.localStorage.getItem('country') === 'JPN' &&
      ['ja', 'en-US'].includes(userLocale)) {
      return !val ? 'YYYY/MM/DD' : '';
    } else {
      return !val ? 'DD/MM/YYYY' : '';
    }
  }
}
